import { NgFor, NgIf } from "@angular/common";
import { HttpEventType } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { SafeResourceUrl } from "@angular/platform-browser";
import { SvgIconComponent } from '@ngneat/svg-icon';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BaseCheckboxTextBoxComponent, GenericServiceResponse, LoaderComponent, NTSTranslatePipe, PopperHelper, TextButtonComponent } from "@nts/std";
import { PDFDocumentProxy, PdfViewerComponent, PdfViewerModule } from "ng2-pdf-viewer";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { BehaviorSubject, Observable, filter, firstValueFrom, lastValueFrom, take, tap } from "rxjs";
import { UploadFileOutputDto } from "src/app/sign-template-long-op/domain-models/dto/upload-file-output.dto";
import { SignerLayoutCollectionItemViewModel } from "src/app/sign-template-long-op/view-models/signer-layout.collection-item-view-model";
import { SignerSettingsCollectionViewModel } from "src/app/sign-template-long-op/view-models/signer-settings.collection-view-model";


export interface FileUploadInterface {
    fileUploadOriginalFileName: string;
    fileUploadStorageName?: string;
    fileUploadFileSize?: number;
    fileObject?: File
    filePreviewUrl: SafeResourceUrl;
}

export interface FileUploadStatusInterface extends FileUploadInterface {
    progress?: number;
}

@UntilDestroy()
@Component({
    selector: 'nts-pdf-preview',
    templateUrl: './pdf-preview.component.html',
    styleUrls: ['./pdf-preview.component.scss'],
    standalone: true,
    imports: [
        TextButtonComponent,
        PdfViewerModule,
        SvgIconComponent,
        NgxPopperjsModule,
        BaseCheckboxTextBoxComponent,
        NgIf,
        LoaderComponent,
        NgFor,
        NTSTranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfPreviewComponent implements OnChanges, OnInit {

    @Input() currentPage: number = 1;
    @Input() isEnabled: boolean = true;
    @Input() externalFile: File;
    @Input() layouts: SignerSettingsCollectionViewModel;
    @Input() uploadOnRemote = false;
    @Input() fileValidationFunc: (pdfDocumentProxy: PDFDocumentProxy) => Promise<boolean> = null;

    internalFile: File;

    /**
        * Altezza fissa del container del canvas
        */
    @Input() initialWrapperHeight = 450;
    @Input() initialWrapperWidth = 450;

    /**
     * Altezza fissa rettangolo in mm
     */
    @Input() rectangleHeight: number = 17;

    /**
     * Larghezza fissa rettangolo in mm
     */
    @Input() rectangleWidth: number = 60;

    @Input() templateSignersColors: string[] = [
        'red',
        'blue',
        'orange',
        'green',
        'black',
        'grey'
    ]

    /**
     * Gestione errori
     */
    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() errorList: string[] = [];

    @Output() fileChanged: EventEmitter<{ storageFileName: string, originalFileName: string }> = new EventEmitter<{ storageFileName: string, originalFileName: string }>()
    @Output() onPdfChanged: EventEmitter<{ proxy: PDFDocumentProxy, file: File }> = new EventEmitter<{ proxy: PDFDocumentProxy, file: File }>()
    @Output() onPageChanged: EventEmitter<number> = new EventEmitter<number>()
    @Input() uploadCallBack: (file: File) => Observable<GenericServiceResponse<UploadFileOutputDto>> = null;
    @Input() canDeleteCallBack: () => Promise<boolean> = null;
    @ViewChild('fileDropRef') fileDropRef;
    @ViewChild('popperError') popperError: NgxPopperjsDirective;
    @ViewChild('queryCanvas') queryCanvas: ElementRef;
    @ViewChild('pdfviewer') pdfViewer: PdfViewerComponent;

    afterLoadCompleted = new BehaviorSubject<boolean>(false);
    pdfLoadedInViewer: boolean = false;
    totalPages: number = 0;
    currentFileData: Uint8Array = null;
    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    proxy: PDFDocumentProxy;
    currentPageView: number[] = null;
    pageWidthInMM: number;
    pageHeightInMM: number;
    currentRectanglesPosition = {};
    onlyPageWithSignatures = true;
    uploadedFile = false;
    uploadFileOnRemoteInProgress = false;
    fileUploadError = null;

    get computedErrorList() {
        return [...this.errorList, this.fileUploadError].filter((error) => error != null)
    }


    /**
     * Altezza calcolata del rettangolo in mm
     */
    calculatedRectangleWidth: number = this.rectangleWidth;

    /**
    * Larghezza calcolata del rettangolo in mm
    */
    calculatedRectangleHeight: number = this.rectangleHeight;

    /**
     * 1 mm = 2.8346456692912997 PDF Point
     */
    pdfPointConversionRatio = 2.8346456692912997;

    constructor(private cd: ChangeDetectorRef) {

    }

    ngOnInit(): void {

        // Randomizzo i colori per i signatari
        if (this.templateSignersColors?.length < this.layouts?.length) {
            for (const [i, v] of this.layouts.entries()) {
                if (this.templateSignersColors[i] == null) {
                    this.templateSignersColors.push(this.randomColor())
                }
            }
        }

        this.checkTempleteSigners();

        this.layouts.collectionChanged.pipe(untilDestroyed(this)).subscribe(() => {
            this.checkTempleteSigners();
        })
    }

    checkTempleteSigners() {
        if (this.layouts == null || this.layouts.length === 0) {
            this.onlyPageWithSignatures = false;
        } else {
            this.onlyPageWithSignatures = this.checkIfCurrentPageHasSignatures()
        }
    }

    // Forzo aggiornamento pdf viewer
    forcePdfUpdate() {
        if (this.pdfViewer) {
            (this.pdfViewer as any).update();
        }
    }

    // Pagina successiva
    nextPage() {
        if (this.onlyPageWithSignatures) {

            let nextPageWithSignature = this.totalPages + 1;

            for (const signer of this.layouts) {
                for (const pos of signer.signaturePositions) {
                    if ((pos.specificPage.value > 0 && pos.specificPage.value > this.currentPage && pos.specificPage.value < nextPageWithSignature)) {
                        nextPageWithSignature = pos.specificPage.value;
                    }
                    if ((pos.lastPage.value === true && this.currentPage != this.totalPages && this.totalPages < nextPageWithSignature)) {
                        nextPageWithSignature = this.totalPages;
                    }
                }
            }
            if (nextPageWithSignature != this.currentPage && nextPageWithSignature <= this.totalPages) {
                this.currentPage = nextPageWithSignature;
                this.onPageChanged.emit(this.currentPage);
            }
        } else {
            this.currentPage++;
            this.onPageChanged.emit(this.currentPage);
        }
        this.cd.detectChanges();
        this.scrollFix();
    }


    // Pagina precedente
    prevPage() {
        if (this.onlyPageWithSignatures) {
            let previousPageWithSignature = 0;
            for (const signer of this.layouts) {
                for (const pos of signer.signaturePositions) {
                    if ((pos.specificPage.value > 0 && pos.specificPage.value < this.currentPage && pos.specificPage.value > previousPageWithSignature)) {
                        previousPageWithSignature = pos.specificPage.value;
                    }
                }
            }
            if (previousPageWithSignature != this.currentPage && previousPageWithSignature > 0) {
                this.currentPage = previousPageWithSignature;
                this.onPageChanged.emit(this.currentPage);
            }

        } else {
            this.currentPage--;
            this.onPageChanged.emit(this.currentPage);
        }
        this.cd.detectChanges();
        this.scrollFix();
    }

    scrollFix() {
        var content = window.document.getElementsByClassName("field-list")[0]
        const actions = window.document.getElementsByClassName("pdf-actions")[0];
        if (content && actions) {
            content.scrollTo({
                top: actions.clientHeight,
                behavior: 'auto'
            })
        }
    }

    // Inizio a disegnare i canvas delle firme dei firmatari
    private initCanvas() {
        const canvas = this.queryCanvas.nativeElement;
        canvas.height = this.initialWrapperHeight;
        canvas.width = canvas.height * this.pageWidthInMM / this.pageHeightInMM;

        if (this.checkIfCurrentPageHasSignatures()) {
            this.adaptRectangleSize();
            this.updateRectanglesPositionFromPdfPoints();
            this.drawRectInCanvas();

        } else {
            const canvas = this.queryCanvas.nativeElement;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        // Forzo aggiornamento visualizzatore pdf
        setTimeout(() => this.forcePdfUpdate());
    }

    /**
    * Disegna il rettangolo nel canvas
    */
    private drawRectInCanvas() {

        const canvas = this.queryCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (const key in this.currentRectanglesPosition) {

            for (const signature of this.currentRectanglesPosition[key]) {
                ctx.beginPath();
                ctx.rect(signature.left, signature.top, signature.width, signature.height);
                ctx.lineWidth = '3';
                ctx.strokeStyle = this.templateSignersColors[key];
                ctx.fillStyle = 'rgba(199, 87, 231, 0.2)';
                ctx.font = "20px Poppins";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.closePath();
                ctx.stroke();
                ctx.fill();

            }
        }
    }

    /**
     * Genera e restituisce un codice colore random
     * @returns codice colore random
     */
    randomColor(): string {
        return "#" + Math.floor(Math.random() * 16777215).toString(16);
    }

    // Restituisce tutte le firme per uno specifico signatario e pagina
    getSignaturesForSpecificPageAndSigner(page: number, signerNumber: number): SignerLayoutCollectionItemViewModel[] {
        const pageIsLastPage = page === this.totalPages;
        const signatures: SignerLayoutCollectionItemViewModel[] = [];

        for (const signature of this.layouts[signerNumber].signaturePositions) {
            if (signature.specificPage.value === page || (pageIsLastPage && signature.lastPage.value)) {
                signatures.push(signature);
            }
        }
        return signatures;
    }

    // Aggiorna la posizione delle firme
    private updateRectanglesPositionFromPdfPoints() {

        this.currentRectanglesPosition = {};
        const canvas = this.queryCanvas.nativeElement;

        const ratio_w = canvas.width / this.pageWidthInMM;
        const ratio_h = canvas.height / this.pageHeightInMM;

        for (const [signerNumber, signer] of this.layouts.entries()) {
            const signatures = this.getSignaturesForSpecificPageAndSigner(this.currentPage, signerNumber)
            if (signatures?.length > 0) {
                this.currentRectanglesPosition[signerNumber] = signatures.map((s) => {

                    const topCoordinateInMM = Math.round(s.topCoordinate.value / this.pdfPointConversionRatio);
                    const leftCoordinateInMM = Math.round(s.leftCoordinate.value / this.pdfPointConversionRatio);

                    return {
                        height: (topCoordinateInMM + (this.calculatedRectangleHeight - topCoordinateInMM)) * ratio_h,
                        width: (leftCoordinateInMM + (this.calculatedRectangleWidth - leftCoordinateInMM)) * ratio_w,
                        top: topCoordinateInMM * ratio_h,
                        left: leftCoordinateInMM * ratio_w
                    }
                })
            }
        }
    }

    // Riadatta la dimensione dei rettangoli
    private adaptRectangleSize() {

        // Riadatto il rettangolo
        if (this.rectangleHeight > this.pageHeightInMM) {
            this.calculatedRectangleHeight = this.pageWidthInMM - 2;
        } else {
            this.calculatedRectangleHeight = this.rectangleHeight;
        }
        if (this.rectangleWidth > this.pageWidthInMM) {
            this.calculatedRectangleWidth = this.pageWidthInMM - 2;
        } else {
            this.calculatedRectangleWidth = this.rectangleWidth;
        }
    }

    // Verifica se la pagina corrente ha almeno una firma
    checkIfCurrentPageHasSignatures(): boolean {
        for (const [signerNumber] of this.layouts.entries()) {
            const signatures = this.getSignaturesForSpecificPageAndSigner(this.currentPage, signerNumber);
            if (signatures?.length > 0) {
                return true;
            }
        }
        return false;
    }

    // Rumuove il file e pulisce tutto
    async removeFile() {

        let canDelete = true;
        if (this.canDeleteCallBack) {
            canDelete = await this.canDeleteCallBack();
        }

        if (this.isEnabled && canDelete) {
            this.internalFile = null;
            this.fileUploadError = null;
            this.afterLoadCompleted.next(false);
            this.externalFile = null;
            this.currentFileData = null;
            this.onPdfChanged.emit({ proxy: null, file: null });
            if (this.fileDropRef?.nativeElement?.value) {
                this.fileDropRef.nativeElement.value = '';
            }
            this.currentPage = 1;
            this.onPageChanged.emit(1);
            this.pdfLoadedInViewer = false;
            this.totalPages = 1;
            this.uploadedFile = false;
            this.checkTempleteSigners();
            this.cd.detectChanges();
        }
    }


    async loadPageData(pageNumber: number) {
        const page = await this.proxy.getPage(pageNumber);
        this.currentPageView = page.view;
        const ratio = this.currentPageView[3] / this.currentPageView[2];
        this.pageWidthInMM = page.view[2] / this.pdfPointConversionRatio;
        this.pageHeightInMM = page.view[3] / this.pdfPointConversionRatio;

        this.initialWrapperWidth = Math.floor(this.initialWrapperHeight / ratio);
        this.initCanvas();
    }

    async afterLoadComplete(proxy: PDFDocumentProxy) {
        this.proxy = proxy;
        this.totalPages = proxy.numPages;
        this.afterLoadCompleted.next(true);
    }


    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['externalFile']?.currentValue) {

            if (changes['externalFile']?.currentValue != null && this.internalFile != changes['externalFile']?.currentValue) {
                this.afterLoadCompleted.next(false);
                this.internalFile = this.externalFile;
                await this.loadPdfFromFile(this.internalFile, false);

            } else if (changes['externalFile']?.currentValue == null) {
                this.removeFile();
            }
        }
        if (changes['errorList']) {
            this.checkPopper();
        }
        if (changes['templateSigners']) {
            this.checkTempleteSigners();
        }
    }

    private checkPopper() {
        if (this.errorList.length === 0) {
            PopperHelper.hide(this.popperError);
        }
    }

    async checkFileValidation(): Promise<boolean> {
        let isValid: boolean;
        if (this.proxy) {
            if (this.fileValidationFunc) {
                isValid = await this.fileValidationFunc(this.proxy);
            } else {
                isValid = true;
            }
        }
        return isValid;
    }

    async loadPdfFromFile(file: File, resetCurrentPage = true): Promise<void> {
        this.pdfLoadedInViewer = false;
        if (file) {
            const buff = await file.arrayBuffer();
            if (resetCurrentPage) {
                this.currentPage = 1;
            }

            this.currentFileData = new Uint8Array(buff);
            // Forzo l'aggiornamento così il viewer carica currentFileData
            this.cd.detectChanges();

            await firstValueFrom(this.afterLoadCompleted.pipe(filter((isComplete) => isComplete), take(1)));
        }
        this.pdfLoadedInViewer = true;
        await this.loadPageData(this.currentPage);
        this.onPdfChanged.emit({ proxy: this.proxy, file: this.internalFile });
        this.cd.detectChanges();
    }

    /**
     * handle file from browsing
     */
    async fileBrowseHandler(e) {
        if (e.target?.files) {

            this.internalFile = e.target?.files[0];
            await this.loadPdfFromFile(this.internalFile);
            const fileIsValid = await this.checkFileValidation();
            if (fileIsValid && this.uploadOnRemote) {
                await this.uploadFiles(e.target.files);
            }
        }
    }

    async onFileDropped(e: any) {
        if (this.isEnabled) {
            this.pdfLoadedInViewer = false;
            this.internalFile = e[0];
            if (this.internalFile) {
                await this.loadPdfFromFile(this.internalFile);
                const fileIsValid = await this.checkFileValidation();
                if (fileIsValid && this.uploadOnRemote) {
                    await this.uploadFiles(e.target.files);
                }
            }
        }
    }

    async uploadFiles(files: FileList) {

        this.uploadFile(files[0])
    }

    progress: number;
    fileUploadStorageName: string;

    async uploadFile(file: File) {
        this.fileChanged.emit({ storageFileName: null, originalFileName: null })
        this.uploadFileOnRemoteInProgress = true;
        this.fileUploadError = null;
        return lastValueFrom(
            this.uploadCallBack(file).pipe(
                tap((httpEvent: any) => {
                    if (httpEvent?.type === HttpEventType.UploadProgress) {
                        this.progress = (Math.round(100 * httpEvent.loaded / httpEvent.total) * 0.75);
                        this.cd.markForCheck();
                    } else if (httpEvent instanceof GenericServiceResponse) {
                        this.progress = 100;
                        this.fileUploadStorageName = httpEvent.result?.storageFileName;
                        if (httpEvent.operationSuccedeed) {
                            this.uploadedFile = true;
                            this.uploadFileOnRemoteInProgress = false;
                            this.fileChanged.emit({ storageFileName: this.fileUploadStorageName, originalFileName: file.name })
                        } else {
                            this.uploadedFile = false;
                            this.fileUploadError = httpEvent?.errors[0];
                            this.uploadFileOnRemoteInProgress = false;
                        }
                        this.cd.markForCheck();
                    } else if (httpEvent?.type === HttpEventType.Sent) {
                        this.progress = 1;
                        this.fileChanged.emit({ storageFileName: null, originalFileName: null })

                        this.cd.markForCheck();
                    }
                })
            )
        )
    }
}
